import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Home,
  Plus,
  Edit2,
  Trash2,
  ArrowLeft,
  X,
  Search,
  Eye,
  Calendar,
} from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../../contexts/AuthContext";
import { createQuartersService } from "../../../services/quartersService";
import { useToast } from "../../../common_components/Toast";
import type {
  IBuilding,
  IFloor,
  IFlat,
  FlatForm,
  IReservationCategory,
  IAllotment,
} from "../types/quarters.types";

const EMPTY_FLAT_FORM: FlatForm = {
  buildingId: "",
  floorId: "",
  flatNumber: "",
  flatName: "",
  area: "",
  bedrooms: 1,
  bathrooms: 1,
  furnished: "unfurnished",
  reservedFor: "",
  status: "vacant",
};

const FlatManagement = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { apiRequest } = useAuth();
  const quartersService = useMemo(() => createQuartersService({ apiRequest }), [apiRequest]);
  const toast = useToast();

  const [flats, setFlats] = useState<IFlat[]>([]);
  const [buildings, setBuildings] = useState<IBuilding[]>([]);
  const [floors, setFloors] = useState<IFloor[]>([]);
  const [reservations, setReservations] = useState<IReservationCategory[]>([]);
  const [loading, setLoading] = useState(true);

  // Pagination & Filtering
  const [filters, setFilters] = useState({
    buildingId: searchParams.get("buildingId") || "",
    floorId: searchParams.get("floorId") || "",
    status: "",
    q: "",
    page: 1,
    limit: 20,
  });
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Flat Modal
  const [isFlatModalOpen, setIsFlatModalOpen] = useState(false);
  const [editingFlat, setEditingFlat] = useState<IFlat | null>(null);
  const [flatForm, setFlatForm] = useState<FlatForm>(EMPTY_FLAT_FORM);

  // Flat Details / History Modal
  const [selectedFlat, setSelectedFlat] = useState<IFlat | null>(null);
  const [flatHistory, setFlatHistory] = useState<IAllotment[]>([]);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);

  // Load supporting master data
  const loadMasterData = useCallback(async () => {
    try {
      const buildsRes = await quartersService.getBuildings();
      if (buildsRes.success) setBuildings(buildsRes.data);

      const resRes = await quartersService.getReservations();
      if (resRes.success) setReservations(resRes.data);

      const preBuilding = searchParams.get("buildingId");
      if (preBuilding) {
        const res = await quartersService.getBuilding(preBuilding);
        if (res.success) setFloors(res.data.floors || []);
      }
    } catch (err) {
      console.error(err);
    }
  }, [quartersService, searchParams]);

  const loadFlats = useCallback(async () => {
    setLoading(true);
    try {
      const res = await quartersService.getFlats({
        ...filters,
        buildingId: filters.buildingId || undefined,
        floorId: filters.floorId || undefined,
        status: filters.status || undefined,
        q: filters.q || undefined,
      });
      if (res.success) {
        setFlats(res.data);
        setTotalPages(res.totalPages || 1);
        setTotalCount(res.total || 0);
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to load flats inventory");
    } finally {
      setLoading(false);
    }
  }, [filters, quartersService, toast]);

  useEffect(() => {
    loadMasterData();
  }, [loadMasterData]);

  useEffect(() => {
    loadFlats();
  }, [loadFlats]);

  // Load floors when building is selected in filter or modal
  const handleFilterBuildingChange = async (buildingId: string) => {
    setFilters((prev) => ({ ...prev, buildingId, floorId: "", page: 1 }));
    if (buildingId) {
      try {
        const res = await quartersService.getBuilding(buildingId);
        if (res.success) {
          setFloors(res.data.floors || []);
        }
      } catch (err) {
        setFloors([]);
      }
    } else {
      setFloors([]);
    }
  };

  const handleModalBuildingChange = async (buildingId: string) => {
    setFlatForm((prev) => ({ ...prev, buildingId, floorId: "" }));
    if (buildingId) {
      try {
        const res = await quartersService.getBuilding(buildingId);
        if (res.success) {
          setFloors(res.data.floors || []);
        }
      } catch (err) {
        setFloors([]);
      }
    } else {
      setFloors([]);
    }
  };

  // Open creation modal
  const handleOpenFlatModal = (flat: IFlat | null = null) => {
    if (flat) {
      setEditingFlat(flat);
      const bId = typeof flat.buildingId === "object" ? (flat.buildingId as IBuilding)._id : flat.buildingId;
      if (bId) {
        quartersService.getBuilding(bId).then((res) => {
          if (res.success) setFloors(res.data.floors || []);
        });
      }
      const fId = typeof flat.floorId === "object" ? (flat.floorId as IFloor)._id : flat.floorId;
      const resId = typeof flat.reservedFor === "object" && flat.reservedFor
        ? (flat.reservedFor as IReservationCategory)._id
        : (flat.reservedFor as string) || "";
      setFlatForm({
        buildingId: bId || "",
        floorId: fId || "",
        flatNumber: flat.flatNumber,
        flatName: flat.flatName || "",
        area: flat.area ? String(flat.area) : "",
        bedrooms: flat.bedrooms || 1,
        bathrooms: flat.bathrooms || 1,
        furnished: flat.furnished || "unfurnished",
        reservedFor: resId,
        status: flat.status,
      });
    } else {
      setEditingFlat(null);
      setFlatForm(EMPTY_FLAT_FORM);
    }
    setIsFlatModalOpen(true);
  };

  const handleFlatSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!flatForm.buildingId || !flatForm.floorId) {
      toast.error("Building and Floor are required");
      return;
    }

    try {
      const { area, reservedFor, ...rest } = flatForm;
      const payload = {
        ...rest,
        area: area ? parseFloat(area) : undefined,
        reservedFor: reservedFor || null,
      } as const;

      if (editingFlat) {
        const res = await quartersService.updateFlat(editingFlat._id, payload);
        if (res.success) {
          toast.success("Flat details updated successfully");
          loadFlats();
          setIsFlatModalOpen(false);
        }
      } else {
        const res = await quartersService.createFlat(payload);
        if (res.success) {
          toast.success("Flat created successfully");
          loadFlats();
          setIsFlatModalOpen(false);
        }
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to save flat");
    }
  };

  const handleDeleteFlat = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this flat?")) return;
    try {
      const res = await quartersService.deleteFlat(id);
      if (res.success) {
        toast.success("Flat deleted successfully");
        loadFlats();
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to delete flat");
    }
  };

  // Occupancy details & history viewer
  const handleOpenHistoryModal = async (flat: any) => {
    try {
      const res = await quartersService.getFlat(flat._id);
      if (res.success) {
        setSelectedFlat(res.data.flat);
        setFlatHistory(res.data.history || []);
        setIsHistoryModalOpen(true);
      }
    } catch (err: any) {
      toast.error("Error loading occupancy records");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50/50 p-6 md:p-10">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
        <div>
          <button
            onClick={() => navigate("/quarters")}
            className="flex items-center gap-2 text-slate-500 hover:text-slate-700 text-sm font-bold mb-2 group transition-colors"
          >
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            Back to Dashboard
          </button>
          <h1 className="text-2xl md:text-3xl font-extrabold text-slate-900 tracking-tight font-serif flex items-center gap-3">
            <Home className="w-8 h-8 text-blue-600" /> Quarters Inventory Management
          </h1>
        </div>
        <button
          onClick={() => handleOpenFlatModal()}
          className="bg-blue-600 hover:bg-blue-700 text-white font-bold px-5 py-3 rounded-xl transition shadow-md shadow-blue-200 flex items-center gap-2"
        >
          <Plus className="w-5 h-5" /> Add Flat/Quarter
        </button>
      </div>

      {/* Filter Options */}
      <div className="bg-white border border-slate-200 p-6 rounded-3xl mb-8 shadow-sm grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
        <div className="space-y-1">
          <label className="text-[10px] font-bold text-slate-400 uppercase">Building</label>
          <select
            value={filters.buildingId}
            onChange={(e) => handleFilterBuildingChange(e.target.value)}
            className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-xs bg-slate-50/50 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
          >
            <option value="">All Buildings</option>
            {buildings.map((b) => (
              <option key={b._id} value={b._id}>
                {b.name} ({b.code})
              </option>
            ))}
          </select>
        </div>

        <div className="space-y-1">
          <label className="text-[10px] font-bold text-slate-400 uppercase">Floor</label>
          <select
            value={filters.floorId}
            onChange={(e) => setFilters({ ...filters, floorId: e.target.value, page: 1 })}
            disabled={!filters.buildingId}
            className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-xs bg-slate-50/50 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition disabled:opacity-50"
          >
            <option value="">All Floors</option>
            {floors.map((f) => (
              <option key={f._id} value={f._id}>
                {f.name}
              </option>
            ))}
          </select>
        </div>

        <div className="space-y-1">
          <label className="text-[10px] font-bold text-slate-400 uppercase">Status</label>
          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value, page: 1 })}
            className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-xs bg-slate-50/50 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
          >
            <option value="">All Statuses</option>
            <option value="vacant">Vacant</option>
            <option value="occupied">Occupied</option>
            <option value="reserved">Reserved</option>
            <option value="maintenance">Under Maintenance</option>
            <option value="locked">Locked</option>
          </select>
        </div>

        <div className="space-y-1 flex gap-2">
          <div className="w-full relative">
            <input
              type="text"
              placeholder="Search flat no..."
              value={filters.q}
              onChange={(e) => setFilters({ ...filters, q: e.target.value })}
              className="w-full border border-slate-200 rounded-xl pl-8 pr-3 py-2.5 text-xs bg-slate-50/50 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
            />
            <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-3.5" />
          </div>
          <button
            onClick={loadFlats}
            className="bg-slate-800 hover:bg-slate-900 text-white px-4 py-2.5 text-xs font-bold rounded-xl transition"
          >
            Apply
          </button>
        </div>
      </div>

      {/* Flats Inventory Grid */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-20 bg-white border border-slate-200 rounded-3xl">
          <div className="animate-spin rounded-full h-10 w-10 border-4 border-blue-600 border-t-transparent"></div>
          <p className="text-slate-500 text-sm font-semibold mt-3">Loading Flats inventory...</p>
        </div>
      ) : flats.length > 0 ? (
        <div className="bg-white border border-slate-200 rounded-3xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-slate-50/50 border-b border-slate-100 text-slate-500 text-xs font-semibold uppercase">
                  <th className="px-6 py-4 text-left">Flat Number</th>
                  <th className="px-6 py-4 text-left">Building & Floor</th>
                  <th className="px-6 py-4 text-left">Details</th>
                  <th className="px-6 py-4 text-left">Reservation</th>
                  <th className="px-6 py-4 text-left">Status</th>
                  <th className="px-6 py-4 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-sm text-slate-700">
                {flats.map((flat) => (
                  <tr key={flat._id} className="hover:bg-slate-50/30 transition">
                    <td className="px-6 py-4 font-bold text-slate-800">{flat.flatNumber}</td>
                    <td className="px-6 py-4">
                      <p className="font-semibold text-slate-800">
                        {typeof flat.buildingId === "object" ? (flat.buildingId as IBuilding).name : "—"}
                      </p>
                      <p className="text-slate-400 text-xs">
                        {typeof flat.floorId === "object" ? (flat.floorId as IFloor).name : "—"}
                      </p>
                    </td>
                    <td className="px-6 py-4 text-xs text-slate-500">
                      <p>Area: {flat.area || "N/A"} sqft</p>
                      <p>
                        Bedrooms: {flat.bedrooms} | Bathrooms: {flat.bathrooms}
                      </p>
                      <p className="capitalize">Furnishing: {flat.furnished}</p>
                    </td>
                    <td className="px-6 py-4">
                      {flat.reservedFor ? (
                        <span className="bg-amber-50 border border-amber-200 text-amber-800 text-xs font-bold px-2 py-0.5 rounded-full uppercase">
                          {typeof flat.reservedFor === "object"
                            ? (flat.reservedFor as IReservationCategory).name
                            : flat.reservedFor}
                        </span>
                      ) : (
                        <span className="text-slate-400 text-xs">Unreserved</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`text-xs px-2.5 py-1 rounded-full font-bold uppercase ${
                          flat.status === "vacant"
                            ? "bg-emerald-100 text-emerald-800"
                            : flat.status === "occupied"
                            ? "bg-indigo-100 text-indigo-800"
                            : flat.status === "reserved"
                            ? "bg-amber-100 text-amber-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {flat.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="flex justify-center items-center gap-2">
                        <button
                          onClick={() => handleOpenHistoryModal(flat)}
                          className="p-1.5 text-slate-500 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition"
                          title="View Occupancy Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleOpenFlatModal(flat)}
                          className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition"
                          title="Edit Flat"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteFlat(flat._id)}
                          className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                          title="Delete Flat"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-between items-center bg-slate-50/50 px-6 py-4 border-t border-slate-100">
              <span className="text-xs text-slate-500">
                Showing {flats.length} of {totalCount} flats
              </span>
              <div className="flex gap-2">
                <button
                  disabled={filters.page === 1}
                  onClick={() => setFilters({ ...filters, page: filters.page - 1 })}
                  className="px-4 py-2 text-xs font-bold text-slate-600 hover:bg-slate-100 border border-slate-200 rounded-xl transition disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  disabled={filters.page === totalPages}
                  onClick={() => setFilters({ ...filters, page: filters.page + 1 })}
                  className="px-4 py-2 text-xs font-bold text-slate-600 hover:bg-slate-100 border border-slate-200 rounded-xl transition disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-20 bg-white border border-slate-200 rounded-3xl">
          <Home className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h2 className="text-lg font-bold text-slate-700">No Flats Configured</h2>
          <p className="text-slate-500 text-sm mt-1">Start by adding your first quarter to the database.</p>
        </div>
      )}

      {/* Flat Create/Edit Modal */}
      {isFlatModalOpen && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white border border-slate-200 rounded-3xl shadow-xl w-full max-w-lg overflow-hidden transform transition-all">
            <div className="flex justify-between items-center bg-slate-50 border-b border-slate-100 px-6 py-4">
              <h3 className="text-slate-800 font-bold text-base font-serif">
                {editingFlat ? "Edit Flat Details" : "Add Flat/Quarter"}
              </h3>
              <button
                onClick={() => setIsFlatModalOpen(false)}
                className="text-slate-400 hover:text-slate-600 p-1.5 rounded-lg hover:bg-slate-100 transition-all"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleFlatSubmit} className="p-6 space-y-4 max-h-[500px] overflow-y-auto">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="block text-xs font-semibold text-slate-500 uppercase">Building*</label>
                  <select
                    required
                    value={flatForm.buildingId}
                    onChange={(e) => handleModalBuildingChange(e.target.value)}
                    className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm bg-slate-50/50 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                  >
                    <option value="">Select Building</option>
                    {buildings.map((b) => (
                      <option key={b._id} value={b._id}>
                        {b.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1">
                  <label className="block text-xs font-semibold text-slate-500 uppercase">Floor*</label>
                  <select
                    required
                    value={flatForm.floorId}
                    onChange={(e) => setFlatForm({ ...flatForm, floorId: e.target.value })}
                    disabled={!flatForm.buildingId}
                    className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm bg-slate-50/50 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition disabled:opacity-50"
                  >
                    <option value="">Select Floor</option>
                    {floors.map((f) => (
                      <option key={f._id} value={f._id}>
                        {f.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="block text-xs font-semibold text-slate-500 uppercase">Flat Number*</label>
                  <input
                    type="text"
                    required
                    value={flatForm.flatNumber}
                    onChange={(e) => setFlatForm({ ...flatForm, flatNumber: e.target.value })}
                    className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm bg-slate-50/50 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                    placeholder="e.g. A101"
                  />
                </div>
                <div className="space-y-1">
                  <label className="block text-xs font-semibold text-slate-500 uppercase">Flat Name</label>
                  <input
                    type="text"
                    value={flatForm.flatName}
                    onChange={(e) => setFlatForm({ ...flatForm, flatName: e.target.value })}
                    className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm bg-slate-50/50 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                    placeholder="e.g. Ground Floor Flat A"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4">
                <div className="space-y-1">
                  <label className="block text-xs font-semibold text-slate-500 uppercase">Area (Sq.Ft)</label>
                  <input
                    type="number"
                    value={flatForm.area}
                    onChange={(e) => setFlatForm({ ...flatForm, area: e.target.value })}
                    className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm bg-slate-50/50 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                    placeholder="e.g. 1200"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-1">
                  <label className="block text-xs font-semibold text-slate-500 uppercase">Bedrooms</label>
                  <input
                    type="number"
                    value={flatForm.bedrooms}
                    onChange={(e) => setFlatForm({ ...flatForm, bedrooms: parseInt(e.target.value) })}
                    className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm bg-slate-50/50 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                    min={1}
                  />
                </div>
                <div className="space-y-1">
                  <label className="block text-xs font-semibold text-slate-500 uppercase">Bathrooms</label>
                  <input
                    type="number"
                    value={flatForm.bathrooms}
                    onChange={(e) => setFlatForm({ ...flatForm, bathrooms: parseInt(e.target.value) })}
                    className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm bg-slate-50/50 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                    min={1}
                  />
                </div>
                <div className="space-y-1">
                  <label className="block text-xs font-semibold text-slate-500 uppercase">Furnished</label>
                  <select
                    value={flatForm.furnished}
                    onChange={(e) => setFlatForm({ ...flatForm, furnished: e.target.value })}
                    className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm bg-slate-50/50 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                  >
                    <option value="unfurnished">Unfurnished</option>
                    <option value="semi-furnished">Semi-Furnished</option>
                    <option value="fully-furnished">Fully-Furnished</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="block text-xs font-semibold text-slate-500 uppercase">Reserved For</label>
                  <select
                    value={flatForm.reservedFor}
                    onChange={(e) => setFlatForm({ ...flatForm, reservedFor: e.target.value })}
                    className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm bg-slate-50/50 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                  >
                    <option value="">No Special Reservation</option>
                    {reservations.map((res) => (
                      <option key={res._id} value={res._id}>
                        {res.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1">
                  <label className="block text-xs font-semibold text-slate-500 uppercase">Status</label>
                  <select
                    value={flatForm.status}
                    onChange={(e) => setFlatForm({ ...flatForm, status: e.target.value })}
                    className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm bg-slate-50/50 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                  >
                    <option value="vacant">Vacant</option>
                    <option value="occupied">Occupied</option>
                    <option value="reserved">Reserved</option>
                    <option value="maintenance">Under Maintenance</option>
                    <option value="locked">Locked</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>

              <div className="flex justify-end gap-2 border-t border-slate-100 pt-4 mt-6">
                <button
                  type="button"
                  onClick={() => setIsFlatModalOpen(false)}
                  className="px-5 py-2.5 text-sm font-bold text-slate-500 hover:bg-slate-100 rounded-xl transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm px-6 py-2.5 rounded-xl transition shadow-md shadow-blue-200"
                >
                  Save Flat
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Flat Occupancy History Modal */}
      {isHistoryModalOpen && selectedFlat && (
        <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white border border-slate-200 rounded-3xl shadow-xl w-full max-w-xl overflow-hidden transform transition-all">
            <div className="flex justify-between items-center bg-slate-50 border-b border-slate-100 px-6 py-4">
              <h3 className="text-slate-800 font-bold text-base font-serif">
                Flat Details & History - {selectedFlat.flatNumber}
              </h3>
              <button
                onClick={() => setIsHistoryModalOpen(false)}
                className="text-slate-400 hover:text-slate-600 p-1.5 rounded-lg hover:bg-slate-100 transition-all"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-6 max-h-[500px] overflow-y-auto">
              {/* Core Specs */}
              <div className="bg-slate-50/50 p-4 border border-slate-100 rounded-2xl grid grid-cols-3 gap-4 text-xs">
                <div>
                  <p className="text-slate-400 font-semibold uppercase">Building</p>
                  <p className="font-bold text-slate-800">
                    {typeof selectedFlat.buildingId === "object"
                      ? (selectedFlat.buildingId as IBuilding).name
                      : "—"}
                  </p>
                </div>
                <div>
                  <p className="text-slate-400 font-semibold uppercase">Floor</p>
                  <p className="font-bold text-slate-800">
                    {typeof selectedFlat.floorId === "object"
                      ? (selectedFlat.floorId as IFloor).name
                      : "—"}
                  </p>
                </div>
                <div>
                  <p className="text-slate-400 font-semibold uppercase">Current Status</p>
                  <p className="font-bold text-slate-800 capitalize">{selectedFlat.status}</p>
                </div>
              </div>

              {/* History Timeline */}
              <div>
                <h4 className="text-slate-800 font-bold text-sm mb-4 font-serif flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-blue-600" /> Occupancy & Actions Timeline
                </h4>
                {flatHistory.length > 0 ? (
                  <div className="space-y-4">
                    {flatHistory.map((item, idx) => (
                      <div key={item._id} className="relative pl-6 pb-2 border-l border-slate-100 last:border-0">
                        {/* Dot */}
                        <div className="absolute -left-1.5 top-1.5 w-3 h-3 bg-blue-500 rounded-full border-2 border-white"></div>
                        <div className="bg-slate-50/30 border border-slate-100 p-4 rounded-xl">
                          <div className="flex justify-between items-center text-xs">
                            <span className="bg-slate-100 text-slate-700 font-bold px-2 py-0.5 rounded-full uppercase">
                              Status: {item.status}
                            </span>
                            <span className="text-slate-400 font-semibold">
                              {new Date(item.allottedAt).toLocaleDateString()}
                            </span>
                          </div>
                          <p className="text-sm font-bold text-slate-800 mt-2">
                             Occupant: {typeof item.userId === "object"
                               ? `${(item.userId as { name: string; email: string }).name} (${(item.userId as { name: string; email: string }).email})`
                               : item.userId}
                           </p>
                          <p className="text-slate-500 text-xs mt-1">
                            Allotment Type: <strong className="capitalize">{item.allotmentType}</strong>
                          </p>
                          {item.remarks && (
                            <p className="text-slate-500 text-xs mt-2 border-t border-slate-100/50 pt-2">
                              <strong>Remarks:</strong> {item.remarks}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 border border-dashed border-slate-200 bg-slate-50/20 rounded-2xl">
                    <p className="text-slate-400 text-xs">No historical occupancy logs found.</p>
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end bg-slate-50 border-t border-slate-100 px-6 py-4">
              <button
                type="button"
                onClick={() => setIsHistoryModalOpen(false)}
                className="px-5 py-2 font-bold text-sm text-slate-600 hover:bg-slate-200 rounded-lg transition"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FlatManagement;